import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import styled from 'styled-components';
import ExcelJS from 'exceljs';
import adminBg from '../Assets/bg/adminbg.jpg';
import Loader from '../Components/Loader';
import useNISData from '../Hooks/NisHooks/useNIS';
import useAddNIS from '../Hooks/NisHooks/useAddNIS';
import useYearNISData from '../Hooks/NisHooks/useYearNISData';
import useDeleteNIS from '../Hooks/NisHooks/useDeleteNis';
import useIAList from '../Hooks/NisHooks/useIAList';
import DeleteConfirmationModal from '../Components/DeleteConfirmationModal';


const NisImport = ({ darkMode, onClose, onSave, selectedYear, setSelectedYear, importedFile, setShowFooter }) => {
  const [importedData, setImportedData] = useState([]);
  const [combinedData, setCombinedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const { data: existingData, loading: dataLoading, refetch } = useNISData(selectedYear);
  const { loading: addLoading, success: addSuccess, error: addError } = useAddNIS();
  const { years: yearData, loading: yearLoading, error: yearError } = useYearNISData();
  const { isName, loading: isNameLoading, error: isNameError } = useIAList();
  const { deleteNIS } = useDeleteNIS();
  const { addNIS } = useAddNIS();
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorList, setErrorList] = useState([]);
  
  const isNames = isName ? isName.map(l => l.is_name.toLowerCase().trim()) : [];

  const handleSave = async () => {
    console.log("handleSave called");
    setIsLoading(true);
    try {
      const recordsToSave = importedData.filter(item => !item.isISname);
      if (recordsToSave.length === 0) {
        alert("No records available to save.");
        setIsLoading(false);
        return;
      }

      let failures = [];
      let successes = 0;

      for (const [index, record] of recordsToSave.entries()) {
        const validationError = validateRecord(record);
        if (validationError) {
          failures.push({
            ia_name: record.ia_name || "(No IA name)",
            reason: `Validation error: ${validationError}`,
          });
          continue;
        }

      const formattedRecord = {
        years: Number(record.years) || selectedYear,
        is_name: record.groupLabel || "",
        ia_name: record.ia_name || "", 
        districtNum: record.districtNum || "",
        municipality: record.municipality || "",
        mailing_address: record.mailing_address || "",
        ia_president: record.ia_president || "",
        contactNum: record.contactNum || "",
        dateOrganized: record.dateOrganized || "",
        secNum: record.secNum || "",
        secDate: record.secDate || "",
        ia_tin: record.ia_tin || "",
        membership_potl: record.membership_potl || "",
        membership_actual: record.membership_actual || "",
        serviceArea: record.serviceArea ? Number(record.serviceArea) : null,
        fusa: record.fusa ? Number(record.fusa) : null, 
        irrigated_area: record.irrigated_area ? Number(record.irrigated_area) : null,
        operational_area: record.operational_area || "",
      };

     try {
        const { success, error } = await addNIS(formattedRecord);
        if (!success) {
          failures.push({
            ia_name: record.ia_name || "(No IA name)",
            reason: error || "Unknown API error",
          });
        } else {
          successes++;
        }
      } catch (err) {
        failures.push({
          ia_name: record.ia_name || "(No IA name)",
          reason: `Exception: ${err.message}`,
        });
      }
    }

    await onSave(recordsToSave);
      await refetch();

      if (successes > 0) {
        alert(successes === recordsToSave.length
          ? "All records saved successfully!"
          : `${successes} of ${recordsToSave.length} records saved successfully.`);
      }
      if (failures.length > 0) {
        setErrorList(failures);
        setErrorModalOpen(true);
      } else {
        onClose(); // Close import UI to return to CISbohol
      }

    } catch (error) {
      console.error("Error in handleSave:", error);
      setErrorList([{ ia_name: "General Error", reason: error.message }]);
      setErrorModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };


  useEffect(() => {
    if (typeof setShowFooter === 'function') {
      setShowFooter(false);
      return () => setShowFooter(true);
    } else {
      console.warn('setShowFooter is not a function:', setShowFooter);
    }
  }, [setShowFooter]);

  useEffect(() => {
    if (importedFile && existingData && !dataLoading && !isNameLoading) {
      const processFile = async () => {
        setIsLoading(true);
        try {
          const workbook = new ExcelJS.Workbook();
          const buffer = await importedFile.arrayBuffer();
          await workbook.xlsx.load(buffer);

          const worksheet = workbook.getWorksheet('NIS');
          if (!worksheet) {
            alert('Invalid file format: Worksheet "NIS" not found.');
            setIsLoading(false);
            return;
          }

          const existingKeys = new Set(
            existingData.map(item => `${item.ia_name}_${item.num}`)
          );

          const newData = [];
          let dataStartRow = 0;
          let currentLabel = null;

          const getCellText = (row, col) => {
            const raw = row.getCell(col).value;
            if (raw === null || raw === undefined) return '';
            if (typeof raw === 'object' && raw.text) return String(raw.text).trim();
            return String(raw).trim();
          };

          const toNumberOrNull = (value) => {
            if (value === null || value === undefined || value === '') return null;
            const parsed = Number(String(value).replace(/,/g, '').trim());
            return Number.isNaN(parsed) ? null : parsed;
          };

          worksheet.eachRow((row, rowNumber) => {
            const rowValues = [];
            for (let i = 1; i <= 17; i++) {
              rowValues.push(getCellText(row, i).toLowerCase());
            }

            const hasMainHeader = rowValues.some(v =>
              v.includes("name of irrigators' association") || v.includes('name of ia')
            );

            if (hasMainHeader) {
              // Template/export has a sub-header row immediately after main headers.
              dataStartRow = rowNumber + 2;
              console.log(`Found NIS main header at row: ${rowNumber}, data starts at: ${dataStartRow}`);
            }
          });

          if (!dataStartRow) {
            alert('Invalid file format: NIS headers were not found.');
            setIsLoading(false);
            return;
          }

          worksheet.eachRow((row, rowNumber) => {
            if (rowNumber >= dataStartRow) {
              const noRaw = getCellText(row, 1);
              const iaNameRaw = getCellText(row, 2);
              const firstCellValue = noRaw.toLowerCase().trim();
              const iaNameLower = iaNameRaw.toLowerCase().trim();

              // Skip totally empty rows.
              const isEmptyRow = [1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17]
                .every(col => getCellText(row, col) === '');
              if (isEmptyRow) return;

              if (
                firstCellValue.includes('subtotal') ||
                firstCellValue.includes('grand total') ||
                iaNameLower.includes('subtotal') ||
                iaNameLower.includes('grand total')
              ) {
                return;
              }

              const isISname = isNames.includes(firstCellValue);
              if (isISname) {
                currentLabel = firstCellValue;
              }

              const rowData = {
                num: noRaw,
                ia_name: iaNameRaw,
                municipality: getCellText(row, 3),
                districtNum: getCellText(row, 4),
                mailing_address: getCellText(row, 5),
                ia_president: getCellText(row, 6),
                contactNum: getCellText(row, 7),
                dateOrganized: getCellText(row, 8),
                secNum: getCellText(row, 9),
                secDate: getCellText(row, 10),
                ia_tin: getCellText(row, 11),
                membership_potl: toNumberOrNull(getCellText(row, 12)),
                membership_actual: toNumberOrNull(getCellText(row, 13)),
                serviceArea: toNumberOrNull(getCellText(row, 14)),
                fusa: toNumberOrNull(getCellText(row, 15)),
                irrigated_area: toNumberOrNull(getCellText(row, 16)),
                operational_area: getCellText(row, 17),
                years: selectedYear?.toString() || '',
                isISname: isISname,
                isImported: true,
                groupLabel: isISname ? firstCellValue : currentLabel
              };

              // Keep only IS label rows or rows with IA name.
              if (!rowData.isISname && !rowData.ia_name) return;

              rowData.isDuplicate = existingKeys.has(
                `${rowData.ia_name}_${rowData.num}`
              );
              newData.push(rowData);
            }
          });

          
          if (newData.length === 0) {
            alert('No valid data to import.');
            setIsLoading(false);
            return;
          }

          setImportedData(newData);
        } catch (err) {
          alert('Failed to import data. Please check the file format.');
        } finally {
          setIsLoading(false);
        }
      };

      processFile();
    }
  }, [importedFile, existingData, dataLoading, isNameLoading, isName, selectedYear]);

  useEffect(() => {
    const combinedDataByLabel = [];

    const existingDataWithLabels = existingData.map((item, index, array) => {
      const isISname = isNames.includes(item.is_name?.toLowerCase().trim());
      let groupLabel = item.is_name?.toLowerCase().trim();
      if (!isISname && index > 0) {
        for (let i = index - 1; i >= 0; i--) {
          if (isNames.includes(array[i].is_name?.toLowerCase().trim())) {
            groupLabel = array[i].is_name.toLowerCase().trim();
            break;
          }
        }
      }
      return { ...item, isImported: false, isISname, groupLabel: groupLabel || null };
    });

    isNames.forEach(label => {
    const is_name = label;
      const isNameRow = importedData.find(item => item.isISname && item.num.toLowerCase().trim() === is_name) ||
                       existingDataWithLabels.find(item => item.isISname && item.is_name?.toLowerCase().trim() === is_name) ||
                       { num: is_name, ia_name: is_name, isISname: true, isImported: false, groupLabel: is_name };

      const existingRows = existingDataWithLabels.filter(item => !item.isISname && item.groupLabel === is_name);
      const importedRows = importedData.filter(item => !item.isISname && item.groupLabel === is_name);

      if (existingRows.length > 0 || importedRows.length > 0) {
        combinedDataByLabel.push(isNameRow, ...existingRows, ...importedRows);
      }
    });

    const ungroupedExisting = existingDataWithLabels.filter(item => !item.groupLabel && !item.isISname);
    if (ungroupedExisting.length > 0) {
      combinedDataByLabel.push(
        { num: 'Ungrouped', ia_name: 'Ungrouped', isISname: true, isImported: false, groupLabel: 'ungrouped' },
        ...ungroupedExisting
      );
    }

    setCombinedData(combinedDataByLabel);
  }, [existingData, importedData, isNames]);

  const validateRecord = (record) => {
    const requiredFields = ['ia_name', 'years'];
    const numericFields = [
      'membership_potl', 'membership_actual', 'serviceArea', 'fusa',
      'irrigated_area'
    ];

    for (const field of requiredFields) {
      if (!record[field] && record[field] !== 0) {
        return `Missing required field: ${field}`;
      }
    }

    for (const field of numericFields) {
      if (record[field] !== null && isNaN(Number(record[field]))) {
        return `Invalid numeric value for ${field}: ${record[field]}`;
      }
    }

    if (isNaN(Number(record.years))) {
      return `Invalid value for years: ${record.years} (expected a number)`;
    }

    return null;
  };

  const handleDeleteClick = (index) => {
    const item = combinedData[index];
    setConfirmDelete({ item, index });
  };

  const handleConfirmDelete = async () => {
    const { item, index } = confirmDelete;
    try {
      if (item.id && !item.isImported) {
        await deleteNIS({ variables: { id: item.id } });
        setCombinedData(prev => prev.filter((_, i) => i !== index));
      } else if (item.isImported) {
        setImportedData(prev => prev.filter((_, i) => 
          `${prev[i].ia_name}` !== 
          `${item.ia_name}`
        ));
      }
    } catch (err) {
      alert('Failed to delete record.');
    } finally {
      setConfirmDelete(null);
    }
  };

  const handleDiscard = () => {
    setImportedData([]);
    const existingDataWithLabels = existingData.map((item, index, array) => {
      const isISname = isNames.includes(item.label?.toLowerCase().trim());
      let groupLabel = item.label?.toLowerCase().trim();
      if (!isISname && index > 0) {
        for (let i = index - 1; i >= 0; i--) {
          if (isNames.includes(array[i].label?.toLowerCase().trim())) {
            groupLabel = array[i].label.toLowerCase().trim();
            break;
          }
        }
      }
      return { ...item, isImported: false, isISname, groupLabel: groupLabel || null };
    });

    const combinedDataByLabel = [];
    isNames.forEach(label => {
       const is_name = label;
      const isNameRow = existingDataWithLabels.find(item => item.isISname && item.is_name?.toLowerCase().trim() === is_name) ||
                       { num: is_name, ia_name: is_name, isISname: true, isImported: false, groupLabel: is_name };
      const existingRows = existingDataWithLabels.filter(item => !item.isISname && item.groupLabel === is_name);
      if (existingRows.length > 0) {
        combinedDataByLabel.push(isNameRow, ...existingRows);
      }
    });

    const ungroupedExisting = existingDataWithLabels.filter(item => !item.groupLabel && !item.isISname);
    if (ungroupedExisting.length > 0) {
      combinedDataByLabel.push(
        { num: 'Ungrouped', ia_name: 'Ungrouped', isISname: true, isImported: false, groupLabel: 'ungrouped' },
        ...ungroupedExisting
      );
    }

    setCombinedData(combinedDataByLabel);
    onClose();
  };

  if (isLoading || dataLoading || addLoading || yearLoading || isNameLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: darkMode ? 'black' : 'white',
        }}
      >
        <Loader />
      </div>
    );
  }

  if (yearError || addError || isNameError) {
    return (
      <div style={{ padding: '20px', color: darkMode ? '#fff' : '#333' }}>
        <p>Error: {yearError?.message || addError?.message || isNameError || 'An unknown error occurred'}</p>
        <StyledButton onClick={() => refetch()}>
          Retry
        </StyledButton>
      </div>
    );
  }

  return (
    <>
      <Container darkMode={darkMode}>
        {addSuccess && <SuccessMessage>{addSuccess}</SuccessMessage>}
        {addError && <ErrorMessage>{addError}</ErrorMessage>}
        <HeaderContainer>
          <RightControls>
            <StyledButton onClick={handleDiscard}>Discard</StyledButton>
            <StyledButton onClick={handleSave} disabled={addLoading || importedData.length === 0}>
              {addLoading ? 'Saving...' : 'Save'}
            </StyledButton>
          </RightControls>
        </HeaderContainer>
        <ColorLegend darkMode={darkMode}>
          <LegendItem color="#ffeb99">New Data</LegendItem>
          <LegendItem color="#ffcccc">Duplicate Data</LegendItem>
          <LegendItem color="#fff">Old Data</LegendItem>
          <LegendItem color="#a3c9a3">Label</LegendItem>
        </ColorLegend>
        <p
          style={{
            padding: '10px 40px',
            margin: 0,
            fontSize: '14px',
            color: darkMode ? '#ccc' : '#555',
            textAlign: 'left',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          Preview of the data from the imported file alongside existing data. New records are highlighted in yellow, duplicates in red, name of IS in green, and existing records in white.
        </p>
        <TableContainer>
          {combinedData.length === 0 ? (
            <NoDataMessage darkMode={darkMode}>
              No data to display. Please check the file and try again.
            </NoDataMessage>
          ) : (
            <TableWrapper id="tableWrapper">
              <div id="scrollContent" style={{ width: '100%', overflowX: 'auto' }}>
                <Table darkMode={darkMode}>
                  <thead>
                    <tr>
                      <TableHeader darkMode={darkMode}>ACTIONS</TableHeader>
                        <TableHeader darkMode={darkMode}>NO</TableHeader>
                        <TableHeader darkMode={darkMode}>NAME OF IRRIGATORS' ASSOCIATION</TableHeader>
                        <TableHeader darkMode={darkMode}>MUNICIPALITY</TableHeader>
                        <TableHeader darkMode={darkMode}>DISTRICT NO.</TableHeader>
                      <TableHeader darkMode={darkMode}>MAILING ADDRESS</TableHeader>
                      <TableHeader darkMode={darkMode}>NAME OF IA PRESIDENT</TableHeader>
                      <TableHeader darkMode={darkMode}>CONTACT NUMBER</TableHeader>
                      <TableHeader darkMode={darkMode}>DATE ORGANIZED</TableHeader>
                      <TableHeader darkMode={darkMode} colSpan="2" style={{ textAlign: 'center' }}>
                        SEC / CDA/DOLE REG. NO.
                      </TableHeader>
                      <TableHeader darkMode={darkMode}>IA TIN</TableHeader>
                      <TableHeader darkMode={darkMode} colSpan="2" style={{ textAlign: 'center' }}>
                        MEMBERSHIP
                      </TableHeader>
                      <TableHeader darkMode={darkMode}>SERVICE AREA</TableHeader>
                      <TableHeader darkMode={darkMode}>FUSA</TableHeader>
                      <TableHeader darkMode={darkMode}>IRRIGATED AREA</TableHeader>
                      <TableHeader darkMode={darkMode}>OPERATIONAL AREA</TableHeader>
                    </tr>
                    <tr>
                      <TableHeaderS darkMode={darkMode}></TableHeaderS>
                      <TableHeaderS colSpan="1" darkMode={darkMode}></TableHeaderS>
                      <TableHeaderS colSpan="7" darkMode={darkMode}></TableHeaderS>
                      <TableHeaderS darkMode={darkMode}>NUMBER</TableHeaderS>
                      <TableHeaderS darkMode={darkMode}>DATE</TableHeaderS>
                      <TableHeaderS colSpan="1" darkMode={darkMode}></TableHeaderS>
                      <TableHeaderS darkMode={darkMode}>POT'L</TableHeaderS>
                      <TableHeaderS darkMode={darkMode}>ACTUAL</TableHeaderS>
                      <TableHeaderS colSpan="4" darkMode={darkMode}></TableHeaderS>
                    </tr>
                  </thead>
                  <tbody>
                    {combinedData.map((item, index) => {
                      const isFirstIA = index === 0 || combinedData[index - 1].ia_name !== item.ia_name;
                      return item.isISname ? (
                        <TableRow key={`is_name-${index}`} isISname={true} darkMode={darkMode}>
                          <TableData
                            colSpan={39}
                            isISname={true}
                            darkMode={darkMode}
                            style={{ fontWeight: 'bold', textAlign: 'left', backgroundColor: '#a3c9a3' }}
                          >
                            {item.num}
                          </TableData>
                        </TableRow>
                      ) : (
                        <TableRow
                          key={`${item.id || `row-${index}`}-${index}`}
                          darkMode={darkMode}
                          isDuplicate={item.isImported && item.isDuplicate}
                          isNew={item.isImported && !item.isDuplicate}
                          isExisting={!item.isImported}
                        >
                          <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px', padding: '8px 0' }}>
                            <ButtonGroup>
                              <StyledButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(index);
                                }}
                              >
                                ❌
                              </StyledButton>
                            </ButtonGroup>
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '80px' }}>
                            {item.num || ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'left', minWidth: '240px' }}>
                            {isFirstIA ? item.ia_name : ''}
                          </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'left', minWidth: '200px' }}>
                                  {item.municipality}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '80px' }}>
                                  {item.districtNum}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'left', minWidth: '200px' }} className="station-mail">
                                  {item.mailing_address}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'left', minWidth: '200px' }} className="station-iaPres">
                                  {item.ia_president}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '200px' }}>
                                  {item.contactNum}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                                  {item.dateOrganized}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }} className="station-secNum">
                                  {item.secNum}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '80px' }} className="station-secDate">
                                  {item.secDate}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }} className="station-iaTin">
                                  {item.ia_tin}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '80px' }}>
                                  {item.membership_potl}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '80px' }}>
                                  {item.membership_actual}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px' }}>
                                  {item.serviceArea}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px' }}>
                                  {item.fusa}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px' }}>
                                  {item.irrigated_area}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px' }}>
                                  {item.operational_area}
                                </TableData>
                        
                        </TableRow>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </TableWrapper>
          )}
        </TableContainer>
      </Container>
      {confirmDelete && (
        <DeleteConfirmationModal
          isOpen={!!confirmDelete}
          onClose={() => setConfirmDelete(null)}
          onConfirm={handleConfirmDelete}
          itemName={confirmDelete.item.ia_name || 'this item'}
          darkMode={darkMode}
        />
      )}
    </>
  );
};

export default NisImport;



// Styled Components
const Container = styled.div`
  background-color: ${({ darkMode }) => (darkMode ? 'transparent' : '#f2f2f2')};
  background-image: ${({ darkMode }) => (darkMode ? `url(${adminBg})` : 'none')};
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  color: ${({ darkMode }) => (darkMode ? '#757272' : '#333')};
  padding: 80px 20px 20px;
  width: 100%;
  min-height: 100vh;
  overflow-y: auto;
  box-sizing: border-box;
  position: relative;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 0 20px;
  margin-top: -40px;
`;

const RightControls = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 20px;
`;

const SuccessMessage = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  background-color: #4caf50;
  color: #fff;
  padding: 10px 20px;
  border-radius: 5px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.2);
  z-index: 9999;
  font-size: 14px;
`;

const ErrorMessage = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  background-color: #f44336;
  color: #fff;
  padding: 10px 20px;
  border-radius: 5px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.2);
  z-index: 9999;
  font-size: 14px;
`;

const StyledButton = styled.button`
  margin: 0 2px;
  padding: 6px 10px;
  background-color: #eee;
  border: 1px solid #aaa;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  font-size: 14px;

  &:hover {
    background-color: #ddd;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  margin-top: 10px;
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  margin-top: 10px;
  position: relative;
  background-color: ${({ darkMode }) => (darkMode ? 'rgb(42, 48, 42)' : '#f2f2f2')};

  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 2500px;
  background-image: ${({ darkMode }) => (darkMode ? `url(${adminBg})` : 'none')};
  color: ${({ darkMode }) => (darkMode ? '#fff' : '#333')};
`;

const TableHeader = styled.th`
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: ${({ darkMode }) => (darkMode ? '#f2f2f2' : '#2e7d32')};
  color: ${({ darkMode }) => (darkMode ? '#333' : '#fff')};
  padding: 10px;
  font-size: 14px;
  border: 1px solid #ddd;
  text-align: center;
  vertical-align: middle;
`;

const TableHeaderS = styled.th`
  position: sticky;
  top: 39px;
  z-index: 1;
  background-color: ${({ darkMode }) => (darkMode ? '#1b5e20' : '#4caf50')};
  color: #ffffff;
  padding: 0 10px;
  border: 1px solid #ddd;
  font-size: 12px;
  text-align: center;
`;

const TableRow = styled.tr`
  background: ${({ isISname, darkMode, isDuplicate, isNew, isExisting }) =>
    isISname
      ? darkMode ? '#4a704a' : '#a3c9a3'
      : isDuplicate
      ? '#ffcccc'
      : isNew
      ? '#ffeb99'
      : isExisting
      ? darkMode ? 'rgba(3, 82, 33, 0.3)' : '#fff'
      : darkMode
      ? 'rgba(3, 82, 33, 0.3)'
      : '#fff'};
`;

const TableData = styled.td`
  padding: 5px;
  font-size: 12px;
  border: 1px solid #ddd;
  color: ${({ darkMode }) => (darkMode ? '#f2f2f2' : '#333')};
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 20px;
  font-size: 16px;
  color: ${({ darkMode }) => (darkMode ? '#fff' : '#333')};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 5px;
  justify-content: center;
`;

const ColorLegend = styled.div`
  padding: 10px 40px;
  display: flex;
  gap: 20px;
  align-items: center;
  background-color: ${({ darkMode }) => (darkMode ? 'rgba(0, 0, 0, 0.5)' : 'rgba(255, 255, 255, 0.8)')};
  border-bottom: 1px solid ${({ darkMode }) => (darkMode ? '#555' : '#ddd')};
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  color: ${({ darkMode }) => (darkMode ? '#fff' : '#333')};

  &:before {
    content: '';
    width: 15px;
    height: 15px;
    background-color: ${({ color }) => color};
    margin-right: 5px;
    border: 1px solid ${({ darkMode }) => (darkMode ? '#666' : '#ccc')};
  }
`;
